import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { calculateItemMetrics } from '@/lib/metrics'
import type { MetricsDataItem } from '@/entities/dailyMetric'

interface PerformanceSummaryCardProps {
  metricsData: MetricsDataItem[]
  startDate: string | null
  endDate: string | null
}

export function PerformanceSummaryCard({ metricsData, startDate, endDate }: PerformanceSummaryCardProps) {
  if (!metricsData || metricsData.length === 0) {
    return null
  }

  // Calculate totals across all dates
  const chartMetrics = metricsData.map((item) => calculateItemMetrics(item, { raw: true }))

  const totalMinutes = chartMetrics.reduce((acc: number, item) => acc + ((item['Total minutes'] as number) || 0), 0)
  const successfulCalls = chartMetrics.reduce((acc: number, item) => acc + ((item['Successful calls'] as number) || 0), 0)
  const connectedCalls = chartMetrics.reduce(
    (acc: number, item) => acc + ((item['Connected to customer'] as number) || 0),
    0
  )
  const totalCost = chartMetrics.reduce((acc: number, item) => acc + ((item['Cost ($)'] as number) || 0), 0)
  const costPerSuccess = successfulCalls > 0 ? totalCost / successfulCalls : 0
  const successRate = connectedCalls > 0 ? (successfulCalls / connectedCalls) * 100 : 0

  // Use fixed-locale number formatters
  const nfInt = new Intl.NumberFormat('en-US')
  const nf1 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })
  const nf2 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })

  function formatDateRangeLabel(): string {
    if (!startDate || !endDate) return `${metricsData.length} days`
    const from = format(new Date(startDate + 'T00:00:00'), 'MMM d')
    const to = format(new Date(endDate + 'T00:00:00'), 'MMM d, yyyy')
    return `${from} – ${to}`
  }

  const dateLabel = formatDateRangeLabel()

  const kpis = [
    {
      label: 'Total Minutes',
      value: nf1.format(totalMinutes),
      subValue: `${metricsData.length} days`,
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
    },
    {
      label: 'Success Count',
      value: nfInt.format(successfulCalls),
      subValue: `${successRate.toFixed(2)}% of connected`,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: 'Success Rate',
      value: `${successRate.toFixed(2)}%`,
      subValue: `${successfulCalls}/${connectedCalls} connected`,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Cost per Success',
      value: `$${nf2.format(costPerSuccess)}`,
      subValue: `Total: $${nf2.format(totalCost)}`,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
  ]

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-heading font-bold">Performance Summary</h2>
        <span className="text-detail text-muted-foreground">{dateLabel}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <div key={index} className={cn('rounded-lg p-4', kpi.bgColor)}>
            <div className="text-body font-medium text-muted-foreground mb-1">{kpi.label}</div>
            <div className={cn('text-heading font-bold mb-1', kpi.color)}>{kpi.value}</div>
            <div className="text-detail text-muted-foreground">{kpi.subValue}</div>
          </div>
        ))}
      </div>
    </div>
  )
}